/** Экран 11 — мои участия, с оформлением призов. */
import type { Claim, Entry } from '@allswipes/client';
import { ApiError } from '@allswipes/client';
import { Badge, BrandMark, Button, Card, Countdown, Empty, Field, Icon, Sheet, Skeleton, fmtShortDay, num, relativeDay } from '@allswipes/ui';
import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { api } from '../api';
import { useReveal } from '../anim';
import { Shell } from '../components/Shell';
import { useResource } from '../hooks';
import { useSession } from '../session';
import { useToast } from '../toast';

import { usePlural, useT } from '@allswipes/i18n';

export function MyEntriesScreen() {
  const t = useT();
  const { signedIn } = useSession();
  const toast = useToast();
  const entries = useResource(() => (signedIn ? api.myEntries() : Promise.resolve({ entries: [] })), [signedIn]);

  // Карточки участий проявляются, когда пришли данные.
  const list = useRef<HTMLDivElement>(null);
  useReveal(list, [entries.data], { selector: '.card' });
  const [claim, setClaim] = useState<Claim | null>(null);
  const [review, setReview] = useState<Claim | null>(null);

  if (!signedIn) {
    return (
      <Shell wide title={t('Мои участия')}>
        <Empty icon="ticket" title={t('Войди, чтобы увидеть свои билеты')}>
          <Link to="/login?next=/my" className="btn pri" style={{ marginTop: 12 }}>
            {t('Войти по почте')}
          </Link>
        </Empty>
      </Shell>
    );
  }

  return (
    <Shell
      wide
      title={t('Мои участия')}
      subtitle={t('билеты, розыгрыши и призы — каждый результат можно пересчитать самому')}
    >
      {entries.loading && <Skeleton count={3} height={110} />}
      {entries.data && entries.data.entries.length === 0 && (
        <Empty icon="ticket" title={t('Пока ни одного участия')}>
          {t('Отсканируй код с упаковки или из чека — и он появится здесь.')}
          <div style={{ marginTop: 12 }}>
            <Link to="/scan" className="btn pri">
              {t('Сканировать код')}
            </Link>
          </div>
        </Empty>
      )}

      <div className="ccards" ref={list}>
        {entries.data?.entries.map((entry) => (
          <EntryCard
            key={entry.campaign.id}
            entry={entry}
            onClaim={setClaim}
            onReview={setReview}
            onChanged={entries.reload}
          />
        ))}
      </div>

      {claim && (
        <ClaimSheet
          claim={claim}
          onClose={() => setClaim(null)}
          onDone={(message) => {
            toast(message);
            setClaim(null);
            entries.reload();
          }}
        />
      )}

      {review && (
        <ReviewSheet
          claim={review}
          onClose={() => setReview(null)}
          onDone={() => {
            toast('Спасибо за отзыв');
            setReview(null);
            entries.reload();
          }}
        />
      )}
    </Shell>
  );
}

function EntryCard({
  entry,
  onClaim,
  onReview,
  onChanged,
}: {
  entry: Entry;
  onClaim: (claim: Claim) => void;
  onReview: (claim: Claim) => void;
  onChanged: () => void;
}) {
  const plural = usePlural();
  const t = useT();
  const { campaign: c, brand, tickets = [], outcomes = [], claims = [] } = entry;
  const toast = useToast();

  const confirmReceived = async (claim: Claim) => {
    try {
      await api.confirmReceived(claim.id);
      toast('Отлично, приз у тебя');
      onChanged();
      onReview(claim);
    } catch (err) {
      toast(err instanceof ApiError ? err.message : t('Не получилось'));
    }
  };

  return (
    <Card>
      <div className="row" style={{ marginBottom: 10 }}>
        <BrandMark name={brand?.name ?? c.prefix} logoUrl={brand?.logo_url} size={30} />
        <Link to={`/c/${c.id}`} className="bname" style={{ flex: 1 }}>
          {brand?.name}
        </Link>
        <StateBadge entry={entry} />
      </div>

      <div className="prize" style={{ fontFamily: 'var(--font-disp)', fontWeight: 600 }}>
        {c.prize_title || c.name}
      </div>

      {tickets.length > 0 && (
        <div className="rail" style={{ marginTop: 10 }}>
          {tickets.map((t) => (
            <span key={t.id} className="badge" style={{ fontFamily: 'var(--font-mono)' }}>
              {t.number}
            </span>
          ))}
        </div>
      )}

      {outcomes.length > 0 && (
        <div className="stack" style={{ marginTop: 10, gap: 6 }}>
          {outcomes.slice(0, 4).map((o) => (
            <div key={o.id} className="row" style={{ fontSize: 13 }}>
              <span>{o.prize_label}</span>
              <span className="spacer" />
              <span className="faint" style={{ fontSize: 11.5 }}>
                {fmtShortDay(o.created_at)}
              </span>
            </div>
          ))}
          {outcomes.length > 4 && (
            <span className="faint" style={{ fontSize: 11.5 }}>
              {t('и ещё {n}', { n: outcomes.length - 4 })}
            </span>
          )}
        </div>
      )}

      {claims.map((claim) => (
        <div
          key={claim.id}
          style={{
            marginTop: 12,
            paddingTop: 12,
            borderTop: '1px solid var(--border)',
          }}
        >
          <div className="row" style={{ marginBottom: 8 }}>
            <Badge tone="prize">{t('выигрыш')}</Badge>
            <span className="spacer" />
            <span className="faint" style={{ fontSize: 11.5 }}>
              {t(claimStatusLabel(claim.status))}
            </span>
          </div>
          <div className="h2">{claim.prize_label}</div>
          {claim.ticket_number && (
            <div className="mono faint" style={{ fontSize: 12, marginTop: 3 }}>
              {t('билет {number}', { number: claim.ticket_number })}
            </div>
          )}

          {claim.status === 'unclaimed' && claim.deadline_at && (
            <p className="sub" style={{ marginTop: 6 }}>
              {t('Оформить до {day}', { day: fmtShortDay(claim.deadline_at) })} ·{' '}
              <Countdown to={claim.deadline_at} prefix={t('осталось')} />
            </p>
          )}
          {claim.pickup_point && (
            <p className="sub" style={{ marginTop: 6 }}>
              {t('Забрать: {point}', { point: claim.pickup_point })}
            </p>
          )}

          <div className="row" style={{ marginTop: 10, gap: 8 }}>
            {claim.status === 'unclaimed' && (
              <Button variant="pri" block onClick={() => onClaim(claim)}>
                {t('Оформить приз')}
              </Button>
            )}
            {claim.status === 'processing' && (
              <span className="sub">{t('Партнёр готовит приз — напишем, когда можно забрать.')}</span>
            )}
            {claim.status === 'ready' && (
              <Button variant="pri" block onClick={() => void confirmReceived(claim)}>
                {t('Забрал приз')}
              </Button>
            )}
            {claim.status === 'received' && (
              <Button variant="out" block onClick={() => onReview(claim)}>
                {t('Оставить отзыв')}
              </Button>
            )}
            {claim.status === 'reviewed' && (
              <span className="sub">
                {t('Отзыв оставлен')}
                {claim.rating ? ` · ${t('{n} из 5', { n: claim.rating })}` : ''}
              </span>
            )}
          </div>
        </div>
      ))}

      <div className="foot" style={{ display: 'flex', marginTop: 12, paddingTop: 11, borderTop: '1px solid var(--border)', fontSize: 12, color: 'var(--faint)' }}>
        {c.status === 'drawn' ? (
          <Link to={`/draw/${c.id}`}>{t('Отчёт о розыгрыше →')}</Link>
        ) : c.draw_at ? (
          <span>{t('розыгрыш {when}', { when: relativeDay(c.draw_at) })}</span>
        ) : (
          <span>{t('до {day}', { day: fmtShortDay(c.end_at) })}</span>
        )}
        <span className="spacer" />
        {tickets.length > 0 && (
          <span className="mono">
            {num(tickets.length)} {plural(tickets.length, 'билет', 'билета', 'билетов')}
          </span>
        )}
      </div>
    </Card>
  );
}

function StateBadge({ entry }: { entry: Entry }) {
  const t = useT();
  switch (entry.state) {
    case 'won':
      return <Badge tone="prize">{t('выигрыш')}</Badge>;
    case 'drawn':
      return <Badge tone="good">{t('итоги')}</Badge>;
    case 'soon':
      return <Badge>{t('скоро')}</Badge>;
    case 'played':
      return <Badge tone="warn">{t('ждём розыгрыш')}</Badge>;
    default:
      return (
        <Badge tone="live" dot>
          {t('идёт')}
        </Badge>
      );
  }
}

function claimStatusLabel(status: Claim['status']): string {
  switch (status) {
    case 'unclaimed':
      return 'нужно оформить';
    case 'processing':
      return 'в обработке';
    case 'ready':
      return 'готов к выдаче';
    case 'received':
      return 'получен';
    case 'reviewed':
      return 'завершён';
  }
}

function ClaimSheet({
  claim,
  onClose,
  onDone,
}: {
  claim: Claim;
  onClose: () => void;
  onDone: (message: string) => void;
}) {
  const t = useT();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const updated = await api.claimPrize(claim.id);
      onDone(
        updated.status === 'ready'
          ? t('Приз доступен сразу — смотри в «Мои участия»')
          : t('Заявка отправлена партнёру'),
      );
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('Не получилось оформить'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet title={t('Оформление приза')} onClose={onClose}>
      <div className="h2">{claim.prize_label}</div>
      {claim.ticket_number && (
        <div className="mono faint" style={{ fontSize: 12, marginTop: 4 }}>
          {t('билет {number}', { number: claim.ticket_number })}
        </div>
      )}

      <ol className="steps" style={{ marginTop: 14 }}>
        <li className="st">
          <span className="n">1</span>
          <div>
            <div className="h2">{t('Подтверди оформление')}</div>
            <div className="sub">
              {claim.fulfil === 'instant'
                ? t('Приз появится в приложении сразу.')
                : t('Партнёр подготовит приз и назовёт точку выдачи.')}
            </div>
          </div>
        </li>
        <li className="st">
          <span className="n">2</span>
          <div>
            <div className="h2">{t('Покажи номер')}</div>
            <div className="sub">{t('В точке выдачи назови номер билета и подтверди личность.')}</div>
          </div>
        </li>
        <li className="st">
          <span className="n">3</span>
          <div>
            <div className="h2">{t('Отметь получение')}</div>
            <div className="sub">{t('И оставь отзыв — он виден бренду в статистике.')}</div>
          </div>
        </li>
      </ol>

      {claim.deadline_at && (
        <p className="sub" style={{ marginTop: 12 }}>
          {t('Срок оформления — до {day}.', { day: fmtShortDay(claim.deadline_at) })}
        </p>
      )}
      {error && (
        <p className="sub" style={{ color: 'var(--bad)', marginTop: 10 }}>
          {error}
        </p>
      )}

      <div style={{ marginTop: 16 }}>
        <Button variant="pri" block disabled={busy} onClick={submit}>
          {busy ? t('Оформляем…') : t('Оформить приз')}
        </Button>
      </div>
    </Sheet>
  );
}

function ReviewSheet({
  claim,
  onClose,
  onDone,
}: {
  claim: Claim;
  onClose: () => void;
  onDone: () => void;
}) {
  const t = useT();
  const [rating, setRating] = useState(0);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (rating === 0) {
      setError(t('Поставь оценку'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.reviewPrize(claim.id, rating, text);
      onDone();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('Не получилось отправить'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet title={t('Как всё прошло?')} onClose={onClose}>
      <p className="sub">{claim.prize_label}</p>
      <div className="stars" style={{ marginTop: 14, justifyContent: 'center' }}>
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            className={n <= rating ? 'on' : ''}
            onClick={() => setRating(n)}
            aria-label={t('{n} из 5', { n })}
          >
            <Icon name="star" size={20} />
          </button>
        ))}
      </div>
      <Field label={t('Что понравилось, что нет')} error={error ?? undefined}>
        <textarea
          className="textarea"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={t('Необязательно')}
          maxLength={2000}
        />
      </Field>
      <Button variant="pri" block disabled={busy} onClick={submit}>
        {busy ? t('Отправляем…') : t('Отправить отзыв')}
      </Button>
    </Sheet>
  );
}
